import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Dialog } from '../ui/Dialog/Dialog';
import { ConfirmDialog } from '../ui/ConfirmDialog/ConfirmDialog';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { Input } from '../atoms/Input/Input';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Text } from '../atoms/Typography/Text';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import { createAlbum, deleteAlbum, renameAlbum } from '../../services/galleryService';
import type { GalleryAlbum } from '../../services/galleryService';
import { useGalleryAlbums, useInvalidateGallery } from '../../hooks/useGalleryOrganisation';
import { useTranslation } from '../../i18n/useTranslation';
import './AlbumManagerDialog.css';

export interface AlbumManagerDialogProps {
  aquariumId: string;
  isOpen: boolean;
  onClose: () => void;
  /** Called after an album was deleted (e.g. to clear an active album filter). */
  onDeleted?: (albumId: string) => void;
}

/** Create, rename and delete the aquarium's gallery albums (#12). */
export function AlbumManagerDialog({ aquariumId, isOpen, onClose, onDeleted }: AlbumManagerDialogProps) {
  const { t } = useTranslation();
  const albums = useGalleryAlbums(aquariumId);
  const invalidate = useInvalidateGallery(aquariumId);
  const newNameRef = useRef<HTMLInputElement>(null);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [toDelete, setToDelete] = useState<GalleryAlbum | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const renameRef = useRef<HTMLInputElement>(null);
  const editingId = editing?.id ?? null;

  const listRef = useRef<HTMLUListElement>(null);
  const lastEditedId = useRef<string | null>(null);

  // Rename happens in place: focus moves into the name field, and back to the album's
  // "Rename" button when the edit is saved or cancelled (WCAG 2.4.3).
  useEffect(() => {
    if (editingId) {
      lastEditedId.current = editingId;
      renameRef.current?.focus();
    } else if (lastEditedId.current) {
      const id = lastEditedId.current;
      lastEditedId.current = null;
      listRef.current?.querySelector<HTMLElement>(`[data-album-rename="${id}"]`)?.focus();
    }
  }, [editingId]);

  const create = useMutation({
    mutationFn: (name: string) => createAlbum(aquariumId, name),
    onSuccess: (album) => {
      setNewName('');
      setAnnouncement(t('gallery.albumManager.created', { name: album.name }));
      invalidate({ albums: true });
      newNameRef.current?.focus();
    },
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameAlbum(aquariumId, id, name),
    onSuccess: (album) => {
      setEditing(null);
      setAnnouncement(t('gallery.albumManager.renamed', { name: album.name }));
      invalidate({ albums: true });
    },
  });

  const remove = useMutation({
    mutationFn: (album: GalleryAlbum) => deleteAlbum(aquariumId, album.id),
    onSuccess: (_d, album) => {
      setToDelete(null);
      setAnnouncement(t('gallery.albumManager.deleted', { name: album.name }));
      invalidate({ albums: true });
      onDeleted?.(album.id);
      newNameRef.current?.focus();
    },
  });

  const handleCreate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const name = newName.trim();
    if (name) create.mutate(name);
  };

  const handleRename = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (editing && editing.name.trim()) rename.mutate({ id: editing.id, name: editing.name.trim() });
  };

  const error = create.isError || rename.isError || remove.isError;

  return (
    <>
      <Dialog
        isOpen={isOpen && toDelete === null}
        onClose={onClose}
        title={t('gallery.albumManager.manageTitle')}
        data-testid="album-manager"
        footer={
          <Button variant="secondary" onClick={onClose}>
            {t('common.close')}
          </Button>
        }
      >
        <div className="album-manager">
          <form className="album-manager__create" onSubmit={handleCreate}>
            <Input
              ref={newNameRef}
              label={t('gallery.albumManager.newName')}
              value={newName}
              maxLength={100}
              onChange={(e) => setNewName(e.target.value)}
            />
            <Button type="submit" variant="primary" isLoading={create.isPending} disabled={create.isPending || !newName.trim()}>
              {t('gallery.albumManager.create')}
            </Button>
          </form>

          {error && <Alert compact>{t('gallery.albumManager.saveError')}</Alert>}

          {albums.error ? (
            <LoadErrorState
              compact
              titleLevel={3}
              title={t('gallery.albumManager.loadError')}
              onRetry={albums.refetch}
              retryLabel={t('common.retry')}
            />
          ) : albums.isLoading ? (
            <Text variant="muted">{t('common.loading')}</Text>
          ) : albums.items.length === 0 ? (
            <Text variant="muted">{t('gallery.albumManager.empty')}</Text>
          ) : (
            <ul className="album-manager__list" ref={listRef}>
              {albums.items.map((album) =>
                editing?.id === album.id ? (
                  <li key={album.id} className="album-manager__item">
                    <form className="album-manager__rename" onSubmit={handleRename}>
                      <Input
                        label={t('gallery.albumManager.renameLabel', { name: album.name })}
                        value={editing.name}
                        maxLength={100}
                        ref={renameRef}
                        onChange={(e) => setEditing({ id: album.id, name: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') {
                            e.stopPropagation();
                            setEditing(null);
                          }
                        }}
                      />
                      <Button type="submit" variant="secondary" size="sm" isLoading={rename.isPending} disabled={!editing.name.trim()}>
                        {t('gallery.albumManager.save')}
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(null)}>
                        {t('common.cancel')}
                      </Button>
                    </form>
                  </li>
                ) : (
                  <li key={album.id} className="album-manager__item">
                    <Text as="span" className="album-manager__name">{album.name}</Text>
                    <span className="album-manager__actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        data-album-rename={album.id}
                        onClick={() => setEditing({ id: album.id, name: album.name })}
                        aria-label={t('gallery.albumManager.renameAction', { name: album.name })}
                      >
                        {t('gallery.albumManager.rename')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setToDelete(album)}
                        aria-label={t('gallery.albumManager.deleteAction', { name: album.name })}
                      >
                        {t('common.delete')}
                      </Button>
                    </span>
                  </li>
                ),
              )}
            </ul>
          )}
          <LiveRegion visuallyHidden>{announcement}</LiveRegion>
        </div>
      </Dialog>

      <ConfirmDialog
        isOpen={toDelete !== null}
        title={t('gallery.albumManager.deleteTitle')}
        message={t('gallery.albumManager.deleteMessage', { name: toDelete?.name ?? '' })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        isConfirming={remove.isPending}
        onConfirm={() => {
          if (toDelete) remove.mutate(toDelete);
        }}
        onCancel={() => setToDelete(null)}
      >
        {remove.isError ? <Alert compact live="off">{t('gallery.albumManager.deleteError')}</Alert> : null}
      </ConfirmDialog>
    </>
  );
}

export default AlbumManagerDialog;
